import React, { useEffect, useState } from 'react';
import {
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import { Controller, useWatch } from 'react-hook-form';
import { phoneCountryCodes } from './ProProfileInfo';
import { SxProps, Theme } from '@mui/material';

interface CommonProps {
  label?: string;
  required?: boolean;
  xs?: number;
  sm?: number;
  md?: number;
  defaultCountryCode?: string;
  disableGridWrapper?: boolean;
  sx?: SxProps<Theme>;
  isDisabled?: boolean;
}

interface CFormProps extends CommonProps {
  useCForm?: true;
  name: string;
  control: any;
}

interface ControlledProps extends CommonProps {
  useCForm?: false;
  value: string;
  onChange: (value: string) => void;
}

type PhoneNumberFieldProps = CFormProps | ControlledProps;

// The HOOK FORM version
const PhoneNumberFieldCForm: React.FC<CFormProps> = ({
  name,
  control,
  label = '',
  required = false,
  xs = 12,
  sm = 6,
  md = 3,
  defaultCountryCode = '+216',
  disableGridWrapper = false,
  isDisabled = false,
  sx = {},
}) => {
  const [selectedCountry, setSelectedCountry] = React.useState(() =>
    phoneCountryCodes.find((c) => c.code === defaultCountryCode) || phoneCountryCodes[0]
  );
  const [localNumber, setLocalNumber] = React.useState('');

  const combinedValue = useWatch({ control, name }) || '';

  React.useEffect(() => {
    if (combinedValue) {
      const match = combinedValue.match(/^(\+\d+)-(.*)$/);
      if (match) {
        const code = match[1];
        const number = match[2];
        const found = phoneCountryCodes.find((c) => c.code === code);
        if (found) setSelectedCountry(found);
        setLocalNumber(number);
      } else {
        setLocalNumber(combinedValue);
      }
    } else {
      setLocalNumber('');
    }
  }, [combinedValue]);

  const content = (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required ? `${label || 'Phone number'} is required` : false,
        validate: (val) => {
          if (!val) return required ? `${label || 'Phone number'} is required` : true;
          return /^\+\d{1,4}-\d{4,}$/.test(val) ? true : 'Invalid phone number';
        },
      }}




      render={({ field, fieldState }) => (
        <TextField
          {...field}
          label={`${label}${required ? '' : ''}`}
          fullWidth
          placeholder="Enter phone number"
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
          value={localNumber}
          disabled={isDisabled}
          onChange={(e) => {
            const val = e.target.value.trim();
            setLocalNumber(val);
            if (!val) {
              field.onChange('');
            } else {
              field.onChange(`${selectedCountry.code}-${val}`);
            }
          }}

          sx={sx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <TextField
                  select
                  value={selectedCountry.code}
                  disabled={isDisabled}
                  onChange={(e) => {
                    const newCountry = phoneCountryCodes.find(
                      (c) => c.code === e.target.value
                    );
                    if (!newCountry) return;
                    setSelectedCountry(newCountry);
                    field.onChange(`${newCountry.code}-${localNumber}`);
                  }}
                  variant="standard"
                  sx={{ minWidth: 40, mr: 1, background: 'none' }}
                  inputProps={{ style: { padding: 0, fontSize: '0.9rem' } }}
                  SelectProps={{
                    renderValue: (selected) => {
                      const found = phoneCountryCodes.find((c) => c.code === selected);
                      return found ? <span className={found.flagClass}></span> : null;
                    },
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 250,
                          minWidth: 120,
                          maxWidth: '90vw',
                          width: 'auto',
                          overflowX: 'auto', // 👈 horizontal scroll
                          whiteSpace: 'nowrap', // 👈 keep flags/country in one line
                          padding: '4px 8px',
                        },
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                    },
                  }}
                >
                  {[...phoneCountryCodes]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((option) => (
                      <MenuItem key={option.code} value={option.code}>
                        <span className={option.flagClass} style={{ marginRight: 8 }}></span>
                        {option.name} ({option.code})
                      </MenuItem>

                    ))}
                </TextField>
              </InputAdornment>
            ),
          }}

        />
      )}
    />
  );

  if (disableGridWrapper) return content;
  return (
    <Grid item xs={xs} sm={sm} md={md}>
      {content}
    </Grid>
  );
};

// The CONTROLLED version
const PhoneNumberFieldControlled: React.FC<ControlledProps> = ({
  value,
  onChange,
  label = '',
  required = false,
  xs = 12,
  sm = 6,
  md = 3,
  defaultCountryCode = '+216',
  disableGridWrapper = false,
  sx = {},
  isDisabled = false,
}) => {
  const [selectedCountry, setSelectedCountry] = React.useState(() =>
    phoneCountryCodes.find((c) => c.code === defaultCountryCode) || phoneCountryCodes[0]
  );
  const [localNumber, setLocalNumber] = React.useState('');

  React.useEffect(() => {
    if (value) {
      const match = value.match(/^(\+\d+)-(.*)$/);
      if (match) {
        const code = match[1];
        const number = match[2];
        const found = phoneCountryCodes.find((c) => c.code === code);
        if (found) setSelectedCountry(found);
        setLocalNumber(number);
      } else {
        setLocalNumber(value);
      }
    } else {
      setLocalNumber('');
    }
  }, [value]);

  const handleCountryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newCode = e.target.value;
    const newCountry = phoneCountryCodes.find((c) => c.code === newCode);
    if (!newCountry) return;
    setSelectedCountry(newCountry);
    onChange(`${newCountry.code}-${localNumber}`);
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newNumber = e.target.value;
    setLocalNumber(newNumber);
    onChange(`${selectedCountry.code}-${newNumber}`);
  };

  const content = (
    <TextField
      label={`${label}${required ? '' : ''}`}
      fullWidth
      placeholder="Enter phone number"
      value={localNumber}
      onChange={handleNumberChange}
      sx={sx}
      disabled={isDisabled}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <TextField
              select
              value={selectedCountry.code}
              onChange={handleCountryChange}
              disabled={isDisabled}
              variant="standard"
              sx={{ minWidth: 40, mr: 1, background: 'none' }}
              inputProps={{ style: { padding: 0, fontSize: '0.9rem' } }}
              SelectProps={{
                renderValue: (selected) => {
                  const found = phoneCountryCodes.find(c => c.code === selected);
                  return found ? <span className={found.flagClass}></span> : null;
                },
                MenuProps: {
                  PaperProps: {
                    style: {
                      maxHeight: 250,
                      minWidth: 120,
                      maxWidth: '90vw',
                      width: 'auto',
                      overflowX: 'auto', // 👈 horizontal scroll
                      whiteSpace: 'nowrap', // 👈 keep flags/country in one line
                      padding: '4px 8px',
                    },
                  },
                  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                  transformOrigin: { vertical: 'top', horizontal: 'left' },
                },
              }}
            >
              {[...phoneCountryCodes]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(option => (
                  <MenuItem key={option.code} value={option.code}>
                    <span className={option.flagClass} style={{ marginRight: 8 }}></span>
                    {option.name} ({option.code})
                  </MenuItem>

                ))}
            </TextField>
          </InputAdornment>
        ),
      }}

    />
  );

  if (disableGridWrapper) return content;
  return (
    <Grid item xs={xs} sm={sm} md={md}>
      {content}
    </Grid>
  );
};

// Main wrapper to pick mode
const PhoneNumberField: React.FC<PhoneNumberFieldProps> = (props) => {
  // default to true if undefined
  const useCForm = props.useCForm !== false;

  if (useCForm) {
    return <PhoneNumberFieldCForm {...(props as CFormProps)} useCForm={true} />;
  }
  return <PhoneNumberFieldControlled {...(props as ControlledProps)} />;
};


export default PhoneNumberField;
